import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import './DashboardInsights.css';

// Reasons an open ticket waits for a human, most urgent first. Each ticket is
// counted under exactly one reason (backend ticket_work_state), so the chips
// always add up to the headline and match the row flags of the ticket list.
// Clicking a chip filters the queue below through the backend
// `attention_reason` parameter — the same classifier that produced the count.
const ATTENTION_REASONS = [
  'reply_to_validate',
  'customer_unanswered',
  'carrier_contact_failed',
  'carrier_contact_to_validate',
  'flagged_action_required'
];

// Daily AI share of resolutions over the last 14 days (mockup: the automation
// cell's trend line). Days without resolutions plot as 0.
const AutomationSparkline = ({ daily, title }) => {
  const points = (daily || []).slice(-14);
  if (points.length < 2) return null;
  const width = 150;
  const height = 26;
  const path = points
    .map((p, i) => {
      const share = p.resolved ? (p.resolved_ai || 0) / p.resolved : 0;
      const x = (i / (points.length - 1)) * width;
      const y = height - 2 - Math.min(share, 1) * (height - 4);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg className="di-sparkline" viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label={title}>
      <title>{title}</title>
      <path d={path} fill="none" stroke="var(--brand-accent)" strokeWidth="1.8" />
    </svg>
  );
};

const fmtFr = (value) => Number(value).toLocaleString('fr-FR');

// KI-242: the backend serves these numbers from a per-account cache (fresh
// 60 s, up to 15 min while a recompute runs). A stale answer is replaced once,
// silently, after the background recompute had time to finish.
const STALE_REFETCH_MS = 4000;

const formatUpdatedAt = (generatedAt) => {
  if (!generatedAt) return null;
  const date = new Date(generatedAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

// Placeholder with the anatomy of the real block (action strip + KPI band),
// shown while the first answer is on its way.
const InsightsSkeleton = () => (
  <div className="di-container di-skeleton" aria-hidden="true">
    <div className="di-skeleton-strip" />
    <div className="di-kpi-grid">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="di-kpi-card di-skeleton-card">
          <span className="di-skeleton-line short" />
          <span className="di-skeleton-line value" />
          <span className="di-skeleton-line" />
        </div>
      ))}
    </div>
  </div>
);

const DashboardInsights = ({ windowDays = 7, activeReason = null, onReasonSelect = null }) => {
  const { t } = useTranslation();
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isSubscribed = true;
    let refetchTimer = null;
    const fetchInsights = async (allowRefetch) => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        const response = await axios.get(`/api/tickets/dashboard-insights?window_days=${windowDays}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!isSubscribed) return;
        setInsights(response.data);
        const cacheState = response.data?.cache?.state || '';
        if (allowRefetch && cacheState.startsWith('stale')) {
          refetchTimer = setTimeout(() => fetchInsights(false), STALE_REFETCH_MS);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard insights:', error);
      } finally {
        if (isSubscribed) setIsLoading(false);
      }
    };
    fetchInsights(true);
    return () => {
      isSubscribed = false;
      if (refetchTimer) clearTimeout(refetchTimer);
    };
  }, [windowDays]);

  if (isLoading) {
    return <InsightsSkeleton />;
  }
  if (!insights) return null;

  const {
    kpis = {}, attention = {}, waiting = {}, aging = null,
    satisfaction = null, series = {}, thresholds = {}
  } = insights;
  const w = insights.window_days || windowDays;
  const oldAfterDays = thresholds.attention_old_after_days ?? 7;
  const attentionOld = attention[`older_than_${oldAfterDays}d`] || 0;
  const reasonsByKey = Object.fromEntries((attention.by_reason || []).map((item) => [item.reason, item]));

  const created = kpis.created_7d?.current;
  const resolved = kpis.resolved_7d?.current;
  const netChange = kpis.open_backlog?.net_change_7d;
  const openBacklog = kpis.open_backlog?.current;
  const openUnassigned = kpis.open_backlog?.unassigned;
  // Meaning-colored: a shrinking backlog is good news whatever the arrow does.
  const netTone = !Number.isFinite(netChange) || netChange === 0 ? 'neutral' : (netChange < 0 ? 'good' : 'bad');
  const netLabel = !Number.isFinite(netChange) || netChange === 0
    ? t('dashboardInsights.netFlat', 'backlog stable')
    : netChange < 0
      ? t('dashboardInsights.netDown', 'backlog en baisse')
      : t('dashboardInsights.netUp', 'backlog en hausse');

  const aiShare = kpis.ai_resolution_share_7d;
  const aiSharePrev = kpis.ai_resolution_share_prev_7d;
  const aiDelta = (aiShare !== null && aiShare !== undefined && aiSharePrev !== null && aiSharePrev !== undefined)
    ? Math.round((aiShare - aiSharePrev) * 10) / 10
    : null;

  const blockedTotal = aging ? (aging.waiting_client_over_72h || 0) + (aging.waiting_carrier_over_48h || 0) : null;
  const waitingCarrier = (waiting.carrier || 0) + (waiting.carrier_untracked || 0);

  const satSample = satisfaction?.sample || 0;
  const satMin = satisfaction?.min_sample ?? 20;
  const showSatisfaction = satisfaction && satisfaction.avg !== null && satisfaction.avg !== undefined && satSample >= satMin;
  const satDelta = showSatisfaction && satisfaction.avg_prev !== null && satisfaction.avg_prev !== undefined
    ? Math.round((satisfaction.avg - satisfaction.avg_prev) * 10) / 10
    : null;

  // The chip lists the tickets of the snapshot whose counter was clicked
  // (backend attention_snapshot), not a newer classification.
  const handleChip = (reason) => {
    if (!onReasonSelect) return;
    if (activeReason === reason) {
      onReasonSelect(null, null);
    } else {
      onReasonSelect(reason, insights.snapshot_id || null);
    }
  };
  const updatedAt = formatUpdatedAt(insights.generated_at);

  return (
    <div className="di-container">
      {attention.total > 0 ? (
        <div className="di-action" role="status">
          <div className="di-action-hero">
            <span className="di-action-icon"><FaExclamationTriangle /></span>
            <span className="di-action-count">{fmtFr(attention.total)}</span>
          </div>
          <div
            className="di-action-text"
            title={t(
              'dashboardInsights.attentionHint',
              'Open tickets whose next step belongs to your team, each counted once under its most urgent reason. Replies sent outside Unanimity (e.g. directly from Outlook) are not visible here.'
            )}
          >
            <strong>{t('dashboardInsights.attentionTitle', 'tickets attendent votre équipe')}</strong>
            {attentionOld > 0 && (
              <span>
                {t('dashboardInsights.attentionOld', 'incl. {{count}} for more than {{days}} days', { count: attentionOld, days: oldAfterDays })}
              </span>
            )}
          </div>
          <div className="di-action-chips">
            {ATTENTION_REASONS.map((reason) => {
              const item = reasonsByKey[reason];
              if (!item || !item.count) return null;
              const old = item[`older_than_${oldAfterDays}d`] || 0;
              const active = activeReason === reason;
              return (
                <button
                  key={reason}
                  type="button"
                  className={`di-action-chip${active ? ' active' : ''}`}
                  aria-pressed={active}
                  onClick={() => handleChip(reason)}
                  title={`${t(`dashboardInsights.reasonHint_${reason}`, '')} ${t('dashboardInsights.reasonOld', '{{count}} for more than {{days}} days.', { count: old, days: oldAfterDays })}`.trim()}
                >
                  <b>{item.count}</b> {t(`dashboardInsights.flag_${reason}`, reason)}
                  <span className="di-chip-arrow" aria-hidden="true">→</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="di-action all-clear" role="status">
          <span className="di-action-icon ok"><FaCheckCircle /></span>
          <span className="di-action-clear">{t('dashboardInsights.allClear', "Aucun ticket n'attend votre équipe")}</span>
        </div>
      )}

      <div className="di-kpi-grid">
        {aiShare !== null && aiShare !== undefined && (
          <div className="di-kpi-card">
            <span className="di-kpi-eyebrow">{t('kpiAutomation', 'Automatisation')} · {w} j</span>
            <div className="di-kpi-value">
              <strong>{String(aiShare).replace('.', ',')}&nbsp;%</strong>
              {aiDelta !== null && aiDelta !== 0 && (
                <span className={`di-delta ${aiDelta > 0 ? 'good' : 'bad'}`}>
                  {t('deltaPts', '{{pts}} pts', { pts: `${aiDelta > 0 ? '+' : ''}${String(aiDelta).replace('.', ',')}` })}
                </span>
              )}
            </div>
            <span className="di-kpi-sub">{t('automationSub', "des résolutions faites par l'IA")}</span>
            {Number.isFinite(resolved) && (
              <span className="di-kpi-note">
                {t('dashboardInsights.automationScope', 'sur {{total}} tickets résolus', { total: fmtFr(resolved) })}
              </span>
            )}
            <AutomationSparkline
              daily={series.daily}
              title={t('dashboardInsights.sparklineHint', '14 derniers jours — créés vs résolus')}
            />
          </div>
        )}
        {Number.isFinite(created) && Number.isFinite(resolved) && (
          <div className="di-kpi-card">
            <span className="di-kpi-eyebrow">{t('kpiFlow', 'Flux')} · {w} j</span>
            <div className="di-kpi-value">
              <strong>{Number.isFinite(netChange) ? `${netChange > 0 ? '+' : ''}${fmtFr(netChange)}` : '—'}</strong>
              {Number.isFinite(netChange) && (
                <span className={`di-delta ${netTone}`}>{netLabel}</span>
              )}
            </div>
            <span className="di-kpi-sub">{t('flowSub', '+{{in}} entrés · −{{out}} résolus', { in: fmtFr(created), out: fmtFr(resolved) })}</span>
            {Number.isFinite(openBacklog) && Number.isFinite(openUnassigned) && (
              <span className="di-kpi-note accent">
                {t('flowOpen', '{{open}} ouverts · {{unassigned}} non assignés', { open: fmtFr(openBacklog), unassigned: fmtFr(openUnassigned) })}
              </span>
            )}
          </div>
        )}
        {aging && (
          <div className="di-kpi-card" title={t('blockedHint', 'Client silencieux depuis plus de 72 h ou transporteur depuis plus de 48 h')}>
            <span className="di-kpi-eyebrow">{t('kpiBlocked', 'Tickets bloqués')}</span>
            <div className="di-kpi-value">
              <strong>{fmtFr(blockedTotal)}</strong>
              {blockedTotal > 0 && <span className="di-delta bad">&gt; 72 h</span>}
            </div>
            <span className="di-kpi-sub">
              {t('blockedSub', '{{client}} attente client · {{carrier}} transporteur', { client: fmtFr(waiting.customer || 0), carrier: fmtFr(waitingCarrier) })}
            </span>
            {aging.oldest_open_days > 0 && (
              <span className="di-kpi-note">{t('blockedOldest', 'plus ancien : {{days}} j', { days: aging.oldest_open_days })}</span>
            )}
          </div>
        )}
        {showSatisfaction && (
          <div className="di-kpi-card" title={t('sentimentHint', "Sentiment estimé par l'IA d'après les messages (0–100)")}>
            <span className="di-kpi-eyebrow">{t('kpiSatisfaction', 'Satisfaction estimée')} · {w} j</span>
            <div className="di-kpi-value">
              <strong>{Math.round(satisfaction.avg)}<span className="di-kpi-unit">/100</span></strong>
              {satDelta !== null && satDelta !== 0 && (
                <span className={`di-delta ${satDelta > 0 ? 'good' : 'bad'}`}>
                  {satDelta > 0 ? '+' : ''}{String(satDelta).replace('.', ',')}
                </span>
              )}
            </div>
            <span className="di-kpi-sub">{t('satisfactionSub', 'sentiment moyen, n = {{n}} tickets', { n: fmtFr(satSample) })}</span>
          </div>
        )}
      </div>
      {updatedAt && (
        <div
          className="di-freshness"
          title={t('dashboardInsights.freshnessHint', 'Chiffres calculés à partir de chaque ticket ouvert à cette heure-là, recalculés automatiquement pendant que le tableau de bord est utilisé.')}
        >
          {t('dashboardInsights.freshness', 'mis à jour à {{time}}', { time: updatedAt })}
        </div>
      )}
    </div>
  );
};

export default DashboardInsights;
